import React, { useState, useEffect } from 'react';
import {
  Modal,
  Image,
  Group,
  ActionIcon,
  Text,
  Stack,
  Badge,
  Card,
  ScrollArea,
} from '@mantine/core';
import {
  IconX,
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconShare,
  IconZoomIn,
  IconZoomOut,
  IconCalendar,
  IconMapPin,
  IconUser,
} from '@tabler/icons-react';
import { useSwipeable } from 'react-swipeable';
import type { ChildPhoto } from '../../../services/parentService';
import { usePhotoDownload } from '../../../hooks/parent/useChildPhotos';
import { renderLocation } from '../../../utils/locationUtils';

interface PhotoViewerProps {
  photos: ChildPhoto[];
  initialIndex: number;
  opened: boolean;
  onClose: () => void;
}

export const PhotoViewer: React.FC<PhotoViewerProps> = ({
  photos,
  initialIndex,
  opened,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const downloadMutation = usePhotoDownload();

  const currentPhoto = photos[currentIndex];

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
    setZoom(1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
    setZoom(1);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!opened) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        handlePrevious();
        break;
      case 'ArrowRight':
        handleNext();
        break;
      case 'Escape':
        onClose();
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [opened, currentIndex]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrevious,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const handleDownload = () => {
    if (currentPhoto) {
      downloadMutation.mutate([currentPhoto.id]);
    }
  };

  const handleShare = () => {
    if (navigator.share && currentPhoto) {
      navigator.share({
        title: `Photo from ${currentPhoto.activity_type}`,
        text: currentPhoto.teacher_notes || 'Check out this photo!',
        url: currentPhoto.url,
      });
    }
  };

  const toggleZoom = () => {
    setZoom(zoom === 1 ? 2 : 1);
  };

  if (!currentPhoto) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      padding={0}
      withCloseButton={false}
      styles={{
        body: { backgroundColor: 'black', height: '100vh', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Group
        justify="space-between"
        p="md"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
        }}
      >
        <Group>
          <ActionIcon
            variant="transparent"
            size="lg"
            onClick={onClose}
            style={{ color: 'white' }}
          >
            <IconX size={24} />
          </ActionIcon>
          <Text c="white" fw={500}>
            {currentIndex + 1} / {photos.length}
          </Text>
        </Group>

        <Group>
          <ActionIcon
            variant="transparent"
            size="lg"
            onClick={toggleZoom}
            style={{ color: 'white' }}
          >
            {zoom === 1 ? <IconZoomIn size={20} /> : <IconZoomOut size={20} />}
          </ActionIcon>
          <ActionIcon
            variant="transparent"
            size="lg"
            onClick={handleShare}
            style={{ color: 'white' }}
          >
            <IconShare size={20} />
          </ActionIcon>
          <ActionIcon
            variant="transparent"
            size="lg"
            onClick={handleDownload}
            style={{ color: 'white' }}
            loading={downloadMutation.isPending}
          >
            <IconDownload size={20} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Main Image */}
      <div
        {...swipeHandlers}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Previous Button */}
        <ActionIcon
          variant="transparent"
          size="xl"
          onClick={handlePrevious}
          style={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10,
          }}
        >
          <IconChevronLeft size={32} />
        </ActionIcon>

        {/* Image */}
        <Image
          src={currentPhoto.url}
          alt={`Photo from ${currentPhoto.activity_type}`}
          fit="contain"
          style={{
            maxHeight: '100%',
            maxWidth: '100%',
            transform: `scale(${zoom})`,
            transition: 'transform 0.3s ease',
            cursor: zoom === 1 ? 'zoom-in' : 'zoom-out',
          }}
          onClick={toggleZoom}
        />

        {/* Next Button */}
        <ActionIcon
          variant="transparent"
          size="xl"
          onClick={handleNext}
          style={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10,
          }}
        >
          <IconChevronRight size={32} />
        </ActionIcon>
      </div>

      {/* Footer Info */}
      <Card
        p="md"
        radius={0}
        style={{
          background: 'rgba(0,0,0,0.8)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <ScrollArea h={120}>
          <Stack gap="sm">
            <Group justify="space-between">
              <Group gap="xs">
                <IconCalendar size={16} style={{ color: 'white' }} />
                <Text size="sm" c="white">
                  {new Date(currentPhoto.timestamp).toLocaleString()}
                </Text>
              </Group>
              <Badge variant="light">{currentPhoto.activity_type}</Badge>
            </Group>

            {currentPhoto.location && (
              <Group gap="xs">
                <IconMapPin size={16} style={{ color: 'white' }} />
                <Text size="sm" c="white">
                  {renderLocation(currentPhoto.location)}
                </Text>
              </Group>
            )}

            {currentPhoto.teacher_notes && (
              <div>
                <Group gap="xs" mb={4}>
                  <IconUser size={16} style={{ color: 'white' }} />
                  <Text size="xs" c="dimmed">Teacher's Note</Text>
                </Group>
                <Text size="sm" c="white" style={{ paddingLeft: 20 }}>
                  {currentPhoto.teacher_notes}
                </Text>
              </div>
            )}

            {currentPhoto.tags && currentPhoto.tags.length > 0 && (
              <Group gap="xs">
                {currentPhoto.tags.map((tag, index) => (
                  <Badge key={index} size="sm" variant="light">
                    {tag}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </ScrollArea>
      </Card>
    </Modal>
  );
};

export default PhotoViewer;